import React, { useCallback, useContext, forwardRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../context/api";
import shortid from "shortid";
import { ArrowRight } from "lucide-react"; 
import { GameContext } from "../context/GameContext";
import { TimerContext } from "../context/TimerContext";
import { AuthContext } from "../context/AuthContext";
import "../styles/PlayGame1.css";

const isHuntexGameName = (gameName = "") => /^huntex/i.test(String(gameName).trim());


export const PlayGame1 = forwardRef(({ setPendingTicket, ...props }, ref) => {
    const navigate = useNavigate();
    const [toast, setToast] = useState({ show: false, message: "", type: "" });
    const [confirmDialog, setConfirmDialog] = useState({ show: false, message: "", onConfirm: null });
    const [insufficientBalanceDialog, setInsufficientBalanceDialog] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const showToast = (message, type = "info") => {
      setToast({ show: true, message, type });
      setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
    };

    const showConfirm = (message, onConfirm) => {
      setConfirmDialog({ show: true, message, onConfirm });
    };
  
    const {
      selectedNumbers,
      againstNumbers,
      minimumWin,
      maximumWin,
      price,
      amount,
      lines,
      result,
      limit,
      limitLabel,
      data,
      validateNumbers,
      resetGame,
    } = useContext(GameContext);
    const { selectedDay } = useContext(TimerContext);
  
    const { token, userData, userBalance, winBalance, fetchUserData, isLoggedIn } = useContext(AuthContext);
    const numericPrice = Number(price) || 0;
    const numericUserBalance = Number(userBalance) || 0;
    // Winnings can be staked as well, so affordability is judged on both
    // accounts combined. The backend spends deposits first, winnings second.
    const numericPlayableBalance = numericUserBalance + (Number(winBalance) || 0);
    const formatMoney = (value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue.toFixed(2) : "0.00";
    };
  
    const confirmInfo = useCallback(() => {
      const selectedGame = selectedDay.d ? selectedDay : selectedDay.sortedGames[0];
      const selectedGameName = selectedGame.d;
      const numericMinimumWin = Number(minimumWin);
      const huntexWinCap = Number(selectedGame?.winCap);

      if (
        isHuntexGameName(selectedGameName) &&
        Number.isFinite(huntexWinCap) &&
        Number.isFinite(numericMinimumWin) &&
        numericMinimumWin > huntexWinCap
      ) {
        showToast(
          `Huntex games have a minimum win of GHS ${huntexWinCap.toLocaleString()}. Please reduce your stake.`,
          "error"
        );
        return;
      }

      if (
        limit.includes(1) &&
        limit.includes(2) &&
        selectedNumbers.length !== 2
      ) {
        showToast("Please select exactly 2 numbers", "error");
        return;
      } else if (
        limit.includes(1) &&
        limit.includes(3) &&
        selectedNumbers.length !== 3
      ) {
        showToast("Please select exactly 3 numbers", "error");
        return;
      } else if (
        limit.includes(3) &&
        limit.includes(24) &&
        (selectedNumbers.length < 3 || selectedNumbers.length > 24)
      ) {
        showToast("Please select between 3 and 24 numbers", "error");
        return;
      } else if (
        limit.includes(4) &&
        limit.includes(17) &&
        (selectedNumbers.length < 4 || selectedNumbers.length > 17)
      ) {
        showToast("Please select between 4 and 17 numbers", "error");
        return;
      } else if (
        limit.includes(5) &&
        limit.includes(13) &&
        (selectedNumbers.length < 5 || selectedNumbers.length > 13)
      ) {
        showToast("Please select between 5 and 13 numbers", "error");
        return;
      } else if (
        limit.includes(6) &&
        limit.includes(12) &&
        (selectedNumbers.length < 6 || selectedNumbers.length > 12)
      ) {
        showToast("Please select between 6 and 12 numbers", "error");
        return;
      } else if (
        limit.includes(1) &&
        limit.includes(24) &&
        (againstNumbers.length < 1 ||
          againstNumbers.length > 24 ||
          selectedNumbers.length < 1 ||
          selectedNumbers.length > 24)
      ) {
        showToast(
          "Please select at least 1 number in both against and selected numbers, and ensure they are between 1 and 24",
          "error"
        );
        return;
      } else if (
        limit.includes(1) &&
        limit.includes(1) &&
        !limit.includes(2) &&
        !limit.includes(3) &&
        !limit.includes(24) &&
        selectedNumbers.length !== 1
      ) {
        showToast("Please select exactly 1 number", "error");
        return;
      } else if (amount < 0.2) {
        showToast("The minimum amount you can stake is 0.2", "error");
        return;
      }
  
      const message = `Selected Game: ${
        selectedGameName
      }
      \nGame Day: ${
        selectedDay.nextGameDate
          ? selectedDay.nextGameDate
          : selectedDay.sortedGames[0].nextGameDate
      } 
      \nchosen Numbers: ${selectedNumbers} 
      \nAgainst Numbers: ${againstNumbers}
      \nPrice: ${price}
      \nMinimum Win: ${minimumWin}
      \nMaximum Win: ${maximumWin}
      \nlimit: ${limitLabel}
      \nLines: ${lines}
      \nAmount: ${amount}`;
  
      showConfirm(message, () => {
        Receipt();
      });
    }, [
      selectedNumbers,
      amount,
      price,
      userBalance,
      winBalance,
      limit,
      selectedDay,
      minimumWin,
      maximumWin,
    ]);
  
    const Receipt = async () => {
      const id = shortid.generate();
      const numbersSaved = await saveNumbersToDatabase(id);
  
      // No toast on failure: saveNumbersToDatabase has already reported the
      // reason, and a second generic one just stacks another popup on it.
      if (numbersSaved) {
        showToast("Game played successfully.", "success");

        // Navigating unmounts this component and takes the toast with it, so
        // hold just long enough for the player to read it before the receipt.
        await new Promise((resolve) => setTimeout(resolve, 1200));

        navigate("/UserReceipt", {
          state: {
            id,
            NextGameDate: selectedDay.nextGameDate
              ? selectedDay.nextGameDate
              : selectedDay.sortedGames[0].nextGameDate,
            selectedDay: selectedDay.d
              ? selectedDay.d
              : selectedDay.sortedGames[0].d,
            closingTime: selectedDay.closingTime
              ? selectedDay.closingTime
              : selectedDay.sortedGames[0].closingTime,
            selectedNumbers,
            againstNumbers,
            price: numericPrice,
            minimumWin,
            maximumWin,
            lines,
            result,
            limit,
             amount,
 
          },
        });
      }
    };
  
    // Did this ticket reach the database? A single indexed lookup, so it
    // answers even when the save itself was too slow to come back.
    const ticketWasStored = async (id) => {
      // Let a save that is still in flight land before looking for it.
      await new Promise((resolve) => setTimeout(resolve, 2000));

      try {
        await api.get(`/shortId/${encodeURIComponent(id)}`);
        return true;
      } catch (error) {
        // 404 is the only answer that proves the ticket is absent; a 400
        // ("id has expired") still describes one that exists. If the check
        // itself could not complete we do not know, so report it missing and
        // let the replay settle it — that is the safe way to be wrong, since
        // the server recognises the id and will not charge twice.
        if (!error.response) return false;
        return error.response.status !== 404;
      }
    };

    const saveNumbersToDatabase = async (id) => {
      try {
        if (!token) {
          showToast("Please log in again to play this game.", "error");
          return false;
        }

        const currentDate = new Date();
        const formattedDate = currentDate.toISOString();
        const payload = {
          id,
          selectedNumbers,
          againstNumbers,
          price: numericPrice,
          amount,
          limit,
          minimumWin,
          maximumWin,
          NextGameDate: selectedDay.nextGameDate
            ? selectedDay.nextGameDate
            : selectedDay.sortedGames[0].nextGameDate,
          selectedDay: selectedDay.d
            ? selectedDay.d
            : selectedDay.sortedGames[0].d,
          closingTime: selectedDay.closingTime
            ? selectedDay.closingTime
            : selectedDay.sortedGames[0].closingTime,
          lines,
          user: {
            name: userData.name,
            email: userData.email,
          },
          date: formattedDate,
        };

        let saveResponse;

        try {
          saveResponse = await api.post("/saveNumbers", payload);
        } catch (error) {
          // A save that never answered may well have been stored anyway — the
          // connection can die while the write still lands. Telling the player
          // it failed when the ticket is in their history is the wrong answer,
          // so find out which happened instead of guessing.
          if (error.response) throw error;
          if (!(await ticketWasStored(id))) {
            // Genuinely not saved. The id makes the replay safe: the server
            // recognises it and will not charge for a second copy.
            saveResponse = await api.post("/saveNumbers", payload);
          }
        }

        // A 200 that reports success: false is still a failure, and announcing
        // a game as played on the strength of the status code alone would be
        // the same mistake in the other direction.
        if (saveResponse && saveResponse.data?.success === false) {
          showToast(
            saveResponse.data.message || "Failed to save numbers. Please try again.",
            "error"
          );
          return false;
        }

        // A stake can come out of both accounts, so refresh them together.
        await fetchUserData();

        return true;
      } catch (error) {
        if (error.response) {
          if (error.response.status === 400) {
            showToast("Insufficient Balance, Please deposit to proceed.", "error");
            navigate("/Home");
          } else {
            // A gateway error page has no JSON message, and a blank toast tells
            // the player nothing.
            showToast(
              error.response.data?.message || "Failed to save numbers. Please try again.",
              "error"
            );
          }
        } else {
          showToast("An error occurred during saving numbers.", "error");
        }
        return false;
      }
    };

    // Helper to build a pending ticket object from current selections
    const buildPendingTicket = () => {
      // Defensive selection of day info: support both shaped selectedDay and nested sortedGames[0]
      const daySource =
        selectedDay && (selectedDay.d || selectedDay.nextGameDate || selectedDay.closingTime)
          ? selectedDay
          : selectedDay && Array.isArray(selectedDay.sortedGames) && selectedDay.sortedGames.length > 0
          ? selectedDay.sortedGames[0]
          : null;

      const lotteryType = (daySource && (daySource.d || daySource.lotteryType)) || "";
      const drawDate = (daySource && (daySource.nextGameDate || daySource.drawDate)) || "";
      const closingTimeVal = (daySource && daySource.closingTime) || "";
      const winCap = Number(daySource?.winCap);

      return {
        id: shortid.generate(),
        lotteryType: lotteryType,
        type: limitLabel || "Direct 3",
        numbers: selectedNumbers,
        againstNumbers: againstNumbers || [],
        price: numericPrice,
        minimumWin: minimumWin || null,
        maximumWin: maximumWin || null,
        lines,
        win:
          minimumWin && !maximumWin
            ? minimumWin
            : maximumWin
            ? maximumWin
            : minimumWin,
        drawDate: drawDate,
        closingTime: closingTimeVal,
        winCap: Number.isFinite(winCap) && winCap > 0 ? winCap : undefined,
        limit: limit || null,
        amount: Number(amount) || 0,
      };
    };

    // Append the current selection to pending tickets and reset the game state
    const addToPending = () => {
      const ticket = buildPendingTicket();
      console.log("Adding to pending ticket:", ticket);
      setPendingTicket((prev) => (Array.isArray(prev) ? [...prev, ticket] : [ticket]));
      if (typeof resetGame === "function") {
        resetGame();
      }
    };

    // Existing play flow: if logged-in, proceed to confirm and play; otherwise add to pending
    const handlePlay = () => {
      const error = validateNumbers(selectedNumbers, againstNumbers, amount, numericPrice, numericPlayableBalance, limit);
      if (error) {
        showToast(error, "error");
        return;
      }

      // Check balance early - show insufficient balance dialog if not enough
      if (numericPlayableBalance < numericPrice) {
        setInsufficientBalanceDialog(true);
        return;
      }

      const selectedGameName = selectedDay.d ? selectedDay.d : selectedDay.sortedGames[0].d;
      const selectedGame = selectedDay.d ? selectedDay : selectedDay.sortedGames[0];
      const numericMinimumWin = Number(minimumWin);
      const huntexWinCap = Number(selectedGame?.winCap);
      if (
        isHuntexGameName(selectedGameName) &&
        Number.isFinite(huntexWinCap) &&
        Number.isFinite(numericMinimumWin) &&
        numericMinimumWin > huntexWinCap
      ) {
        showToast(
          `Huntex games have a minimum win cap of GHS ${huntexWinCap.toLocaleString()}. Please reduce your stake.`,
          "error"
        );
        return;
      }

      if (isLoggedIn) {
        confirmInfo();
      } else {
        addToPending();
      }
    };

    // New: Always add current selection to pending (works for logged-in and logged-out)
    const handleAddToPending = () => {
      const error = validateNumbers(selectedNumbers, againstNumbers, amount, numericPrice, numericPlayableBalance, limit);
      if (error) {
        showToast(error, "error");
        return;
      }
      addToPending();
    };

   

  return (
    <>
      <div className="numbers-container">
        {isLoggedIn && (
          <button className="pay-touch" onClick={handleAddToPending}>
            Add to Pending <ArrowRight size={20} style={{ marginLeft: 8, verticalAlign: "middle" }} />
          </button>
        )}
        <button className="pay-touch" onClick={handlePlay} style={{ marginLeft: isLoggedIn ? 12 : 0 }}>
          Play Game <ArrowRight size={20} style={{ marginLeft: 8, verticalAlign: "middle" }} />
        </button>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.show && (
        <div className="confirm-overlay" onClick={() => setConfirmDialog({ show: false, message: "", onConfirm: null })}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Confirm Game</h3>
            <div className="confirm-message">
              {confirmDialog.message.split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            <div className="confirm-buttons">
              <button 
                className="confirm-btn cancel-btn"
                onClick={() => setConfirmDialog({ show: false, message: "", onConfirm: null })}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn proceed-btn"
                onClick={async () => {
                  setIsProcessing(true);
                  setConfirmDialog({ show: false, message: "", onConfirm: null });
                  if (confirmDialog.onConfirm) {
                    await confirmDialog.onConfirm();
                  }
                  setIsProcessing(false);
                }}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient Balance Dialog */}
      {insufficientBalanceDialog && (
        <div className="confirm-overlay" onClick={() => setInsufficientBalanceDialog(false)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Insufficient Balance</h3>
            <div className="confirm-message">
              <div>Your current balance is GHS {formatMoney(numericPlayableBalance)}</div>
              <div>You need GHS {formatMoney(price)} to play this game.</div>
              <div style={{ marginTop: '16px' }}>Please deposit to continue playing.</div>
            </div>
            <div className="confirm-buttons">
              <button 
                className="confirm-btn cancel-btn"
                onClick={() => setInsufficientBalanceDialog(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn proceed-btn"
                onClick={() => {
                  setInsufficientBalanceDialog(false);
                  navigate("/Deposit");
                }}
              >
                Top up now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

